import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import supabase from "../supabase";
import { orderObjectStatus, selectableOrderStatus } from "../constants/orderStatus";
import { ticketObjectStatus } from "../constants/ticketStatus";
import { getCurrentEmployeeId } from "../utils/currentEmployee";

const CHECK_PAYMENT_METHOD = 7;
const CHECK_STATE = { pendiente: 0, cobrado: 1, rechazado: 2 };
const ATTENTION_LIMIT = 60;
const BUCKET_ORDENES = "orders";

export const RANGES = [
  { id: "hoy", label: "Hoy", days: 0 },
  { id: "semana", label: "7 días", days: 7 },
  { id: "mes", label: "Mes", days: 30 },
];

const rangeStart = (rangeId) => {
  if (rangeId === "hoy") return moment().startOf("day");
  const range = RANGES.find((r) => r.id === rangeId) || RANGES[2];
  return moment().subtract(range.days, "days").startOf("day");
};

const orderTotal = (order) =>
  (parseFloat(order.subTotal) || 0) +
  (parseFloat(order.iva105) || 0) +
  (parseFloat(order.iva210) || 0);

const ticketTotal = (ticket) =>
  (ticket.ticketDetails || []).reduce((acc, detail) => {
    const subtotal = (parseFloat(detail.price) || 0) * (parseFloat(detail.quantity) || 0);
    return acc + subtotal + subtotal * ((parseFloat(detail.ivaPercentage) || 0) / 100);
  }, 0);

/**
 * Motivo por el que una orden está frenada. El orden importa: se muestra el
 * primero que aplica, que es el que el administrador tiene que destrabar.
 */
const attentionReason = (order) => {
  if (order.status === orderObjectStatus.solicitudDeCancelación) {
    return { text: "Cancelación a aprobar", tone: "danger" };
  }
  if (order.status === orderObjectStatus.listoParaFacturar) {
    const days = moment().diff(moment(order.createdAt), "days");
    return { text: `Esperando factura hace ${days} ${days === 1 ? "día" : "días"}`, tone: "warn" };
  }
  if (!order.employeeId) return { text: "Sin operador asignado", tone: "danger" };
  if (!order.clientSignaturePhoto) return { text: "Sin firma del cliente", tone: "danger" };
  if (!order.bucketId) return { text: "Sin fotos cargadas", tone: "muted" };
  return null;
};

/** Métricas del panel de administración. Todas salen de campos que ya existen. */
export const useAdminMetrics = (rangeId = "mes") => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const since = rangeStart(rangeId).toISOString();

      const [statusCountResults, ordersRes, cashRes, checksRes, ticketsRes] = await Promise.all([
        Promise.all(
          selectableOrderStatus.map(({ value }) =>
            supabase
              .from("orders")
              .select("orderId", { count: "exact", head: true })
              .eq("status", value)
              .gte("createdAt", since)
              .then(({ count, error: err }) => ({ value, count: err ? 0 : count || 0 }))
          )
        ),
        supabase
          .from("orders")
          .select(
            "orderId, createdAt, status, subTotal, iva105, iva210, employeeId, bucketId, clientSignaturePhoto, client: clientId(company)"
          )
          .in("status", [
            orderObjectStatus.pendiente,
            orderObjectStatus.terminado,
            orderObjectStatus.listoParaFacturar,
            orderObjectStatus.solicitudDeCancelación,
          ])
          .order("orderId", { ascending: false })
          .limit(300),
        supabase.from("cash").select("cashId, description, balance").order("cashId"),
        supabase
          .from("paymentDetails")
          .select("checkAmount, checkState, checkNumber, checkBank, checkIssuerName, checkPaymentDate:checkPaymentDate::text")
          .eq("paymentMethod", CHECK_PAYMENT_METHOD),
        supabase
          .from("tickets")
          .select("ticketId, status, ticketDate, client: clientId(company), ticketDetails(price, quantity, ivaPercentage)")
          .eq("status", ticketObjectStatus.autorizada),
      ]);

      if (ordersRes.error) throw new Error(ordersRes.error.message);

      const orders = ordersRes.data || [];

      // Listas para facturar: plata frenada, con la antigüedad de la más vieja.
      const toInvoice = orders.filter((o) => o.status === orderObjectStatus.listoParaFacturar);
      const toInvoiceAmount = toInvoice.reduce((acc, o) => acc + orderTotal(o), 0);
      const oldestToInvoice = toInvoice.reduce((oldest, o) => {
        const created = moment(o.createdAt);
        return !oldest || created.isBefore(oldest) ? created : oldest;
      }, null);

      // Cobranza: facturas autorizadas sin pagar, separando lo vencido a más de 30 días.
      const tickets = ticketsRes.error ? [] : ticketsRes.data || [];
      const receivable = tickets.reduce((acc, t) => acc + ticketTotal(t), 0);
      const overdue = tickets
        .filter((t) => moment().diff(moment(t.ticketDate), "days") > 30)
        .reduce((acc, t) => acc + ticketTotal(t), 0);

      // Caja: saldo por caja, tal como lo lleva la tabla `cash`.
      const cashes = cashRes.error ? [] : cashRes.data || [];
      const cashTotal = cashes.reduce((acc, c) => acc + (parseFloat(c.balance) || 0), 0);

      // Cheques en cartera: los pendientes de cobro, con vencimiento y rechazos.
      const checks = checksRes.error ? [] : checksRes.data || [];
      const pendingChecks = checks.filter((c) => Number(c.checkState) === CHECK_STATE.pendiente);
      const checksAmount = pendingChecks.reduce((acc, c) => acc + (parseFloat(c.checkAmount) || 0), 0);
      const rejectedChecks = checks.filter((c) => Number(c.checkState) === CHECK_STATE.rechazado);
      const rejectedAmount = rejectedChecks.reduce(
        (acc, c) => acc + (parseFloat(c.checkAmount) || 0),
        0
      );
      const upcomingChecks = pendingChecks
        .filter((c) => c.checkPaymentDate)
        .map((c) => ({ ...c, dueDate: moment(c.checkPaymentDate) }))
        .filter((c) => c.dueDate.isValid())
        .sort((a, b) => a.dueDate.valueOf() - b.dueDate.valueOf());

      const attention = orders
        .map((order) => ({ order, reason: attentionReason(order) }))
        .filter((row) => row.reason)
        .slice(0, ATTENTION_LIMIT);

      const statusCounts = statusCountResults.reduce((acc, row) => {
        acc[row.value] = row.count;
        return acc;
      }, {});

      setData({
        statusCounts,
        statusTotal: Object.values(statusCounts).reduce((a, b) => a + b, 0),
        toInvoice: {
          amount: toInvoiceAmount,
          count: toInvoice.length,
          oldestDays: oldestToInvoice ? moment().diff(oldestToInvoice, "days") : null,
        },
        receivable: { amount: receivable, count: tickets.length, overdue },
        cash: { total: cashTotal, boxes: cashes },
        checks: {
          amount: checksAmount,
          count: pendingChecks.length,
          rejectedCount: rejectedChecks.length,
          rejectedAmount,
          upcoming: upcomingChecks,
        },
        attention,
      });
    } catch (err) {
      setError(err.message || "No se pudieron cargar las métricas");
    } finally {
      setLoading(false);
    }
  }, [rangeId]);

  useEffect(() => {
    load();
  }, [load]);

  return { data, loading, error, reload: load };
};

/**
 * Órdenes que tienen al menos una foto.
 *
 * No hay columna en la base que lo diga: las fotos viven en Storage bajo
 * `orders/<orderId>/…`, y una carpeta sólo aparece en el listado si tiene
 * archivos adentro. Así que una sola pasada por la raíz del bucket alcanza
 * para saber cuáles tienen fotos, en vez de un `list()` por orden.
 *
 * OJO: `bucketId` NO sirve para esto. Esa columna guarda el nombre del bucket
 * y se escribe con el literal "orders" en toda orden, tenga fotos o no.
 */
const PAGINA_STORAGE = 1000;

const fetchOrderIdsConFotos = async () => {
  const ids = new Set();
  for (let offset = 0; ; offset += PAGINA_STORAGE) {
    const { data, error } = await supabase.storage
      .from(BUCKET_ORDENES)
      .list("", { limit: PAGINA_STORAGE, offset });

    if (error) throw new Error(error.message);
    (data || []).forEach((entry) => ids.add(entry.name));
    if (!data || data.length < PAGINA_STORAGE) return ids;
  }
};

/**
 * Métricas del operador. Sólo ve órdenes pendientes (el resto de los estados
 * está oculto para su rol), así que las tres cifras miran lo que le falta cerrar.
 */
export const useOperatorMetrics = () => {
  const [orders, setOrders] = useState([]);
  const [orderIdsConFotos, setOrderIdsConFotos] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // "Tus órdenes": son las del operador logueado, no las de todos.
      const propio = await getCurrentEmployeeId();
      if (propio === null) {
        throw new Error("No se pudo identificar al operador de la sesión");
      }

      const { data, error: err } = await supabase
        .from("orders")
        // Sin `orderDetails`: la pantalla de edición los pide por su cuenta, así
        // que traerlos acá sería cargar hasta 100 órdenes de datos al pedo.
        .select(
          "orderId, createdAt, status, subTotal, iva105, iva210, bucketId, clientSignaturePhoto, employeeId, client: clientId(company, name, lastName)"
        )
        .eq("status", orderObjectStatus.pendiente)
        .eq("employeeId", propio)
        .order("orderId", { ascending: false })
        .limit(100);

      if (err) throw new Error(err.message);
      setOrders(data || []);
      setOrderIdsConFotos(await fetchOrderIdsConFotos());
    } catch (e) {
      setError(e.message || "No se pudieron cargar tus órdenes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const buckets = useMemo(() => {
    const withAmount = orders.map((o) => ({ ...o, totalAmount: orderTotal(o) }));
    return {
      pendientes: withAmount,
      firma: withAmount.filter((o) => !o.clientSignaturePhoto),
      // Si el listado de Storage falló, se prefiere no acusar falta de fotos
      // sobre órdenes que quizás sí las tengan.
      fotos: orderIdsConFotos
        ? withAmount.filter((o) => !orderIdsConFotos.has(String(o.orderId)))
        : [],
    };
  }, [orders, orderIdsConFotos]);

  return { buckets, loading, error, reload: load };
};

export { orderTotal, ticketTotal };
